// file: frontend/src/components/pages/admin/IndividualsTab.jsx
import React, { useEffect, useState, useMemo } from "react";
import { getAllPlanUpgradeRequests, approvePlanUpgradeRequest, rejectPlanUpgradeRequest, getAllIndividualUsersPaginated, updateUserStatus, deleteUser, bulkDeleteUsers } from "../../../../api/apiFunction/userServices";
import { toast } from "react-toastify";
import { Check, X as Cross, ChevronLeft, ChevronRight, UserPlus, Upload, Trash2, Search, X } from "lucide-react";
import ToggleSwitch from "../../../ui/ToggleSwitch";
import CreateUserModal from "./CreateUserModal";
import BulkUploadModal from "./BulkUploadModal";

const PAGE_SIZE = 10;

// Skeleton loader for action buttons, filter bar, table rows, and pagination
const TableSkeleton = ({ columns = 9, rows = 6 }) => (
  <div>
    {/* Action buttons skeleton */}
    <div className="flex justify-end gap-3 mb-4 animate-pulse">
      <div className="h-9 w-32 bg-bg-40 rounded-md" />
      <div className="h-9 w-32 bg-bg-40 rounded-md" />
    </div>

    {/* Filter bar skeleton */}
    <div className="flex w-full items-end gap-3 mb-4 animate-pulse">
      {/* Search input skeleton */}
      <div className="flex-1 min-w-[200px]">
        <div className="h-4 w-16 bg-bg-40 rounded mb-2" />
        <div className="h-9 w-full bg-bg-40 rounded" />
      </div>
      {/* Filter button skeleton */}
      <div>
        <div className="h-9 w-20 bg-bg-40 rounded" />
      </div>
      {/* Clear button skeleton */}
      <div>
        <div className="h-9 w-16 bg-bg-40 rounded" />
      </div>
      {/* Plan dropdown skeleton */}
      <div className="min-w-[100px]">
        <div className="h-4 w-12 bg-bg-40 rounded mb-2" />
        <div className="h-9 w-full bg-bg-40 rounded" />
      </div>
      {/* Status dropdown skeleton */}
      <div className="min-w-[100px]">
        <div className="h-4 w-14 bg-bg-40 rounded mb-2" />
        <div className="h-9 w-full bg-bg-40 rounded" />
      </div>
      {/* Filter button skeleton */}
      <div>
        <div className="h-10 w-20 bg-bg-40 rounded" />
      </div>
      {/* Clear button skeleton (conditional) */}
      <div>
        <div className="h-10 w-16 bg-bg-40 rounded" />
      </div>
      {/* Plan dropdown skeleton */}
      <div className="min-w-[100px]">
        <div className="h-4 w-1/2 bg-bg-40 rounded mb-2" />
        <div className="h-10 w-full bg-bg-40 rounded" />
      </div>
      {/* Status dropdown skeleton */}
      <div className="min-w-[100px]">
        <div className="h-4 w-1/2 bg-bg-40 rounded mb-2" />
        <div className="h-10 w-full bg-bg-40 rounded" />
      </div>
    </div>

    {/* Table skeleton */}
    <div className="w-full flex justify-center">
      <div className="max-w-7xl w-full">
        <div className="bg-bg-60 rounded-lg shadow-sm overflow-x-auto">
          <table className="min-w-full divide-y divide-bd-50">
            <thead className="bg-bg-40">
              <tr>
                {[...Array(columns)].map((_, i) => (
                  <th key={i} className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    <div className="h-4 w-16 bg-bg-40 rounded animate-pulse" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-bg-60 divide-y divide-bd-50">
              {[...Array(rows)].map((_, i) => (
                <tr key={i}>
                  {[...Array(columns)].map((_, j) => (
                    <td key={j} className="px-4 py-4">
                      <div className="h-6 w-full bg-bg-40 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>

    {/* Pagination skeleton */}
    <div className="flex justify-center items-center gap-1 mt-4 pb-6 animate-pulse">
      <div className="h-8 w-8 bg-bg-40 rounded" />
      {[...Array(5)].map((_, i) => (
        <div key={i} className="h-8 w-8 bg-bg-40 rounded" />
      ))}
      <div className="h-8 w-8 bg-bg-40 rounded" />
    </div>
  </div>
);

const IndividualsTab = ({ hideTitle }) => {
  const [tab, setTab] = useState("users");

  // Users state
  const [users, setUsers] = useState([]);
  console.log("users", users);
  const [usersLoading, setUsersLoading] = useState(true); // Start with true to show loader on mount
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);

  // Requests state
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");

  // Modal state (for requests)
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [modalRequestId, setModalRequestId] = useState(null);
  const [adminResponse, setAdminResponse] = useState("");
  const [confirming, setConfirming] = useState(false);

  // Create User Modal state
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);

  // Bulk Upload Modal state
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);

  // Delete User Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Bulk selection state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // Search & filter state for users
  const [userSearch, setUserSearch] = useState("");
  console.log("userSearch", userSearch);
  const [activeSearch, setActiveSearch] = useState(""); // Track the active search term
  const [userPlanFilter, setUserPlanFilter] = useState("");
  const [userStatusFilter, setUserStatusFilter] = useState("");

  // Search & filter state for requests
  const [reqSearch, setReqSearch] = useState("");
  const [reqCurrentPlan, setReqCurrentPlan] = useState("");
  const [reqRequestedPlan, setReqRequestedPlan] = useState("");
  const [reqStatus, setReqStatus] = useState("");

  // Unique plan/status values for dropdowns
  const userPlanOptions = useMemo(() => Array.from(new Set(users.map(u => u.plan?.name).filter(Boolean))), [users]);
  const userStatusOptions = ["Active", "Inactive"];
  const reqCurrentPlanOptions = useMemo(() => Array.from(new Set(requests.map(r => r.currentPlan?.name).filter(Boolean))), [requests]);
  const reqRequestedPlanOptions = useMemo(() => Array.from(new Set(requests.map(r => r.requestedPlan?.name).filter(Boolean))), [requests]);
  const reqStatusOptions = ["pending", "approved", "rejected"];

  // Filtered users - only apply plan and status filters (search is handled by backend)
  const filteredUsers = useMemo(() => {
    const filtered = users.filter(user => {
      const matchesPlan = !userPlanFilter || user.plan?.name === userPlanFilter;
      const matchesStatus = !userStatusFilter || (userStatusFilter === "Active" ? (user.status === true || user.status === "active") : !(user.status === true || user.status === "active"));
      return matchesPlan && matchesStatus;
    });
    
    // Sort by createdAt (most recent first)
    return filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [users, userPlanFilter, userStatusFilter]);

  // Filtered requests (remove requestType filter here, since it's now filtered at fetch)
  const filteredRequests = useMemo(() => Array.isArray(requests) ? requests.filter(req => {
    const matchesSearch = (req.user?.name || "").toLowerCase().includes(reqSearch.toLowerCase()) || (req.user?.email || "").toLowerCase().includes(reqSearch.toLowerCase());
    const matchesCurrentPlan = !reqCurrentPlan || req.currentPlan?.name === reqCurrentPlan;
    const matchesRequestedPlan = !reqRequestedPlan || req.requestedPlan?.name === reqRequestedPlan;
    const matchesStatus = !reqStatus || req.status === reqStatus;
    return matchesSearch && matchesCurrentPlan && matchesRequestedPlan && matchesStatus;
  }) : [], [requests, reqSearch, reqCurrentPlan, reqRequestedPlan, reqStatus]);

  // Fetch users
  const fetchUsers = async (page = 1, search = '') => {
    console.log('🔍 fetchUsers called with:', { page, search });
    setUsersLoading(true);
    try {
      const params = { 
        page, 
        limit: PAGE_SIZE 
      };
      
      // Add search query if exists
      if (search && search.trim()) {
        params.search = search.trim();
        console.log('✅ Adding search to params:', params);
      } else {
        console.log('❌ No search term, params:', params);
      }
      
      console.log('📡 API call params:', params);
      const res = await getAllIndividualUsersPaginated(params);
      setUsers(res?.data?.users || []);
      setUsersTotalPages(res?.data?.pagination?.totalPages || 1);
    } catch {
      setUsers([]);
      setUsersTotalPages(1);
    } finally {
      setUsersLoading(false);
    }
  };

  // Fetch requests
  const fetchRequests = async () => {
    setRequestsLoading(true);
    try {
      const res = await getAllPlanUpgradeRequests();
      // Only user requests, case-insensitive, from res.data
      setRequests(Array.isArray(res?.data) ? res.data.filter(r => (r.requesterType || r.requestType || '').toLowerCase() === 'user') : []);
    } catch {
      setRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  };

  // Tab effect
  useEffect(() => {
    console.log('🔄 useEffect triggered, tab:', tab, 'usersPage:', usersPage, 'activeSearch:', activeSearch);
    if (tab === "users") {
      fetchUsers(usersPage, activeSearch); // Use activeSearch instead of userSearch
    } else {
    fetchRequests();
    }
  }, [tab, usersPage, activeSearch]); // Add activeSearch to dependencies

  // Handle filter button click
  const handleFilter = () => {
    console.log('🔘 Filter button clicked, userSearch:', userSearch);
    setActiveSearch(userSearch); // Set the active search term
    setUsersPage(1); // Reset to page 1 when filtering
    console.log('✅ activeSearch will be set to:', userSearch);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setUserSearch('');
    setActiveSearch(''); // Clear the active search term
    setUsersPage(1);
  };

  // Handle delete user
  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  // Confirm delete user
  const confirmDeleteUser = async () => {
    if (!userToDelete || deleteLoading) return;

    setDeleteLoading(true);
    try {
      await deleteUser(userToDelete._id);
      toast.success("User deleted successfully");
      setShowDeleteModal(false);
      setUserToDelete(null);
      fetchUsers(usersPage);
    } catch (error) {
      toast.error("Failed to delete user");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle select all users
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUsers(filteredUsers.map(u => u._id));
    } else {
      setSelectedUsers([]);
    }
  };

  // Handle select single user
  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (selectedUsers.length === 0) {
      toast.error("Please select users to delete");
      return;
    }
    setShowBulkDeleteModal(true);
  };

  // Confirm bulk delete
  const confirmBulkDelete = async () => {
    if (selectedUsers.length === 0 || bulkDeleteLoading) return;

    setBulkDeleteLoading(true);
    try {
      await bulkDeleteUsers(selectedUsers);
      toast.success(`${selectedUsers.length} user(s) deleted successfully`);
      setShowBulkDeleteModal(false);
      setSelectedUsers([]);
      fetchUsers(usersPage);
    } catch (error) {
      toast.error("Failed to delete users");
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // Clear selection when page changes
  useEffect(() => {
    setSelectedUsers([]);
  }, [usersPage]);
  // Modal logic (same as before)
  const openModal = (type, requestId) => {
    setModalType(type);
    setModalRequestId(requestId);
    setAdminResponse(type === 'approve' ? "Plan upgrade approved. Welcome to your new plan!" : "Plan upgrade rejected. Please provide more details about your usage requirements.");
    setModalOpen(true);
    setConfirming(false);
  };
  const closeModal = () => {
    setModalOpen(false);
    setModalType(null);
    setModalRequestId(null);
    setAdminResponse("");
    setConfirming(false);
  };
  const handleModalSubmit = async () => {
    if (!adminResponse.trim()) {
      toast.error("Please enter a response message.");
      return;
    }
    setActionLoading(modalRequestId);
    try {
      if (modalType === 'approve') {
        await approvePlanUpgradeRequest(modalRequestId, adminResponse);
        toast.success("Request approved");
      } else {
        await rejectPlanUpgradeRequest(modalRequestId, adminResponse);
        toast.success("Request rejected");
      }
      fetchRequests();
      closeModal();
    } catch {
      toast.error(`Failed to ${modalType} request`);
    } finally {
      setActionLoading("");
    }
  };

  const handleUserCreated = () => {
    fetchUsers(usersPage);
  };

  const handleBulkUploadSuccess = () => {
    fetchUsers(usersPage);
  };

  return (
    <div className="h-full p-6 overflow-y-auto custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        {!hideTitle && <h2 className="text-2xl font-semibold mb-6">Individuals</h2>}
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            className={`py-1.5 px-4 text-sm font-medium transition-colors duration-150 border-none outline-none focus:outline-none rounded-lg 
              ${tab === 'users' 
                ? 'bg-bg-40 text-fg-90 border-b-2 border-ac-02 shadow-sm' 
                : 'bg-transparent text-fg-60 hover:text-fg-80'}
            `}
            style={{ boxShadow: tab === 'users' ? '0 1px 4px 0 rgba(0,0,0,0.03)' : 'none' }}
            onClick={() => setTab('users')}
          >
            All Users
          </button>
          <button
            className={`py-1.5 px-4 text-sm font-medium transition-colors duration-150 border-none outline-none focus:outline-none rounded-lg 
              ${tab === 'requests' 
                ? 'bg-bg-40 text-fg-90 border-b-2 border-ac-02 shadow-sm' 
                : 'bg-transparent text-fg-60 hover:text-fg-80'}
            `}
            style={{ boxShadow: tab === 'requests' ? '0 1px 4px 0 rgba(0,0,0,0.03)' : 'none' }}
            onClick={() => setTab('requests')}
          >
            Upgrade Requests
          </button>
        </div>
        {tab === "users" ? (
          usersLoading ? (
            <TableSkeleton columns={9} rows={6} />
          ) : (
            <>
              {/* Create User and Upload CSV Buttons */}
              <div className="flex justify-end gap-3 mb-4">
                <button
                  onClick={() => setBulkUploadModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-fg-50 bg-bg-50 border border-bd-50 rounded-md hover:bg-bg-40 transition-colors"
                >
                  <Upload size={18} />
                  Upload CSV
                </button>
                <button
                  onClick={() => setCreateUserModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-ac-02 rounded-md hover:bg-ac-01 transition-colors"
                >
                  <UserPlus size={18} />
                  Create User
                </button>
              </div>

              {/* Search & Filters for Users */}
              <div className="flex w-full items-end gap-3 mb-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-fg-60 mb-1">Search</label>
                  <input
                    type="text"
                    className="w-full py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                    placeholder="Search by name, or email..."
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && handleFilter()}
                    disabled={usersLoading}
                  />
                </div>
                <div>
                  <button
                    onClick={handleFilter}
                    disabled={usersLoading}
                    className="flex items-center gap-2 px-4 py-1.5 text-xs font-medium bg-ac-02 hover:bg-ac-01 text-white dark:text-black rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Search size={14} />
                    {usersLoading ? 'Searching...' : 'Filter'}
                  </button>
                </div>
                {userSearch && (
                  <div>
                    <button
                      onClick={handleClearSearch}
                      disabled={usersLoading}
                      className="flex items-center gap-2 px-4 py-1.5 text-xs font-medium bg-bg-50 hover:bg-bg-40 text-fg-50 border border-bd-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <X size={14} />
                      Clear
                    </button>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-fg-60 mb-1">Plan</label>
                  <select
                    className="py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                    value={userPlanFilter}
                    onChange={e => setUserPlanFilter(e.target.value)}
                  >
                    <option value="">All</option>
                    {userPlanOptions.map(plan => <option key={plan} value={plan}>{plan.charAt(0).toUpperCase() + plan.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-fg-60 mb-1">Status</label>
                  <select
                    className="py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                    value={userStatusFilter}
                    onChange={e => setUserStatusFilter(e.target.value)}
                  >
                    <option value="">All</option>
                    {userStatusOptions.map(status => <option key={status} value={status}>{status}</option>)}
                  </select>
                </div>
                {/* Bulk Delete Button - Only visible when users are selected */}
                {selectedUsers.length > 0 && (
                  <div className="flex items-end">
                    <button
                      onClick={handleBulkDelete}
                      className="flex items-center gap-2 px-4 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                    >
                      <Trash2 size={14} />
                      Delete ({selectedUsers.length})
                    </button>
                  </div>
                )}
              </div>

              <div className="w-full flex justify-center">
                <div className="max-w-7xl w-full">
                  <div className="bg-bg-60 rounded-lg shadow-sm overflow-x-auto custom-scrollbar">
                    <table className="min-w-full divide-y divide-bd-50">
                      <thead className="bg-bg-40">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider w-12">
                            <input
                              type="checkbox"
                              checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                              className="w-4 h-4 rounded border-bd-50 text-ac-02 focus:ring-ac-02 focus:ring-2 cursor-pointer"
                            />
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Account Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Organization</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Plan</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Chat Usage</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Thread Usage</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Created At</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-bg-60 divide-y divide-bd-50">
                        {filteredUsers.length === 0 ? (
                          <tr>
                            <td colSpan="11" className="text-center text-fg-60 py-12">No users found.</td>
                          </tr>
                        ) : (
                          filteredUsers.map((user) => (
                            <tr key={user._id} className={selectedUsers.includes(user._id) ? 'bg-ac-02/5' : ''}>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={selectedUsers.includes(user._id)}
                                  onChange={(e) => handleSelectUser(user._id, e.target.checked)}
                                  className="w-4 h-4 rounded border-bd-50 text-ac-02 focus:ring-ac-02 focus:ring-2 cursor-pointer"
                                />
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-fg-80 font-semibold">{user.name}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-xs text-fg-60">{user.email}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-xs text-fg-60">
                                {(() => {
                                  const accountType = user.accountType || 'regular';
                                  const badgeClass = accountType === 'sponsored' 
                                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200' 
                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-900/40 dark:text-gray-200';
                                  return (
                                    <span className={`px-2 py-1 rounded-md text-xs font-semibold ${badgeClass}`.trim()}>
                                      {accountType.charAt(0).toUpperCase() + accountType.slice(1)}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-xs text-fg-60">
                                {user.sponsorInfo?.organization || 'N/A'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-fg-60">{
                                (() => {
                                  const planName = (user.plan?.name || "Free").toLowerCase();
                                  let badgeClass = "bg-gray-100 text-gray-600 dark:bg-gray-900/40 dark:text-gray-200";
                                  if (planName === "free") badgeClass = "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/40 dark:text-yellow-200";
                                  else if (planName === "pro") badgeClass = "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-200";
                                  else if (planName === "enterprise") badgeClass = "bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-200";
                                  return (
                                    <span className={`px-2 py-1 rounded-md text-xs font-semibold ${badgeClass}`.trim()}>
                                      {user.plan?.name || "Free"}
                                    </span>
                                  );
                                })()
                              }</td>
                              <td className="px-4 py-3 whitespace-nowrap text-xs text-fg-60">{user.chatUsage}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-xs text-fg-60">{user.threadLimitUsage}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-xs text-fg-60">
                                {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric' 
                                }) : 'N/A'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <ToggleSwitch
                                  checked={user.status === true || user.status === 'active'}
                                  onChange={async (isChecked) => {
                                    // Optimistically update UI
                                    setUsers((prev) => prev.map((u) => u._id === user._id ? { ...u, status: isChecked } : u));
                                    try {
                                      await updateUserStatus(user._id, isChecked);
                                      toast.success(`User status updated to ${isChecked ? 'Active' : 'Inactive'}`);
                                    } catch {
                                      toast.error('Failed to update user status');
                                      // Revert UI if failed
                                      setUsers((prev) => prev.map((u) => u._id === user._id ? { ...u, status: !isChecked } : u));
                                    }
                                  }}
                                  label={user.status === true || user.status === 'active' ? 'Active' : 'Inactive'}
                                />
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <button
                                  onClick={() => handleDeleteUser(user)}
                                  className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 group transition-colors"
                                  title="Delete User"
                                >
                                  <Trash2 size={16} className="text-fg-60 group-hover:text-red-600 transition-colors" />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            {/* Pagination moved outside the table card */}
              <div className="flex justify-center items-center gap-1 mt-4 pb-6">
                <button
                  className="p-1.5 rounded hover:bg-bg-40 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
                  onClick={() => setUsersPage((p) => Math.max(1, p - 1))}
                  disabled={usersPage === 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={20} />
                </button>
                {(() => {
                  const pages = [];
                  const totalPages = usersTotalPages;
                  const current = usersPage;
                  
                  // Always show first page
                  pages.push(1);
                  
                  if (totalPages <= 7) {
                    // Show all pages if 7 or less
                    for (let i = 2; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    // Show ellipsis logic
                    if (current <= 3) {
                      // Near start: 1 2 3 4 ... last
                      for (let i = 2; i <= 4; i++) pages.push(i);
                      pages.push('...');
                      pages.push(totalPages);
                    } else if (current >= totalPages - 2) {
                      // Near end: 1 ... n-3 n-2 n-1 n
                      pages.push('...');
                      for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
                    } else {
                      // Middle: 1 ... current-1 current current+1 ... last
                      pages.push('...');
                      pages.push(current - 1);
                      pages.push(current);
                      pages.push(current + 1);
                      pages.push('...');
                      pages.push(totalPages);
                    }
                  }
                  
                  return pages.map((page, idx) => 
                    page === '...' ? (
                      <span key={`ellipsis-${idx}`} className="px-3 py-1.5 text-fg-60">...</span>
                    ) : (
                      <button
                        key={page}
                        className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                          page === current 
                            ? 'bg-ac-02 text-white' 
                            : 'hover:bg-bg-40 text-fg-60'
                        }`}
                        onClick={() => setUsersPage(page)}
                      >
                        {page}
                      </button>
                    )
                  );
                })()}
                <button
                  className="p-1.5 rounded hover:bg-bg-40 disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
                  onClick={() => setUsersPage((p) => Math.min(usersTotalPages, p + 1))}
                  disabled={usersPage === usersTotalPages}
                  aria-label="Next page"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </>
          )
        ) : (
          // Upgrade Requests Tab (existing code)
          requestsLoading ? (
            <TableSkeleton columns={8} rows={6} />
          ) : (
            <>
              {/* Search & Filters for Requests */}
              <div className="flex w-full items-end gap-3 mb-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-fg-60 mb-1">Search</label>
                  <input
                    type="text"
                    className="w-full py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                    placeholder="Search by name or email..."
                    value={reqSearch}
                    onChange={e => setReqSearch(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-fg-60 mb-1">Current Plan</label>
                  <select
                    className="py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                    value={reqCurrentPlan}
                    onChange={e => setReqCurrentPlan(e.target.value)}
                  >
                    <option value="">All</option>
                    {reqCurrentPlanOptions.map(plan => <option key={plan} value={plan}>{plan.charAt(0).toUpperCase() + plan.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-fg-60 mb-1">Requested Plan</label>
                  <select
                    className="py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                    value={reqRequestedPlan}
                    onChange={e => setReqRequestedPlan(e.target.value)}
                  >
                    <option value="">All</option>
                    {reqRequestedPlanOptions.map(plan => <option key={plan} value={plan}>{plan.charAt(0).toUpperCase() + plan.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-fg-60 mb-1">Status</label>
                  <select
                    className="py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                    value={reqStatus}
                    onChange={e => setReqStatus(e.target.value)}
                  >
                    <option value="">All</option>
                    {reqStatusOptions.map(status => <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              <div className="w-full flex justify-center">
                <div className="max-w-7xl w-full">
                  <div className="bg-bg-60 rounded-lg shadow-sm overflow-x-auto custom-scrollbar">
                    <table className="min-w-full divide-y divide-bd-50">
                      <thead className="bg-bg-40">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Current Plan</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Requested Plan</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Request Message</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Admin Response</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-bg-60 divide-y divide-bd-50">
                        {filteredRequests.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center text-fg-60 py-12">No plan upgrade requests found.</td>
                          </tr>
                        ) : (
                          filteredRequests.map((req) => (
                            <tr key={req._id}>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-fg-80 font-semibold">{req.user?.name}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-xs text-fg-60">{req.user?.email}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-fg-60">{
                                (() => {
                                  const planName = (req.currentPlan?.name || "Free").toLowerCase();
                                  let badgeClass = "bg-gray-100 text-gray-600 dark:bg-gray-900/40 dark:text-gray-200";
                                  if (planName === "free") badgeClass = "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/40 dark:text-yellow-200";
                                  else if (planName === "pro") badgeClass = "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-200";
                                  else if (planName === "enterprise") badgeClass = "bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-200";
                                  return (
                                    <span className={`px-2 py-1 rounded-md text-xs font-semibold ${badgeClass}`.trim()}>
                                      {req.currentPlan?.name || "Free"}
                                    </span>
                                  );
                                })()
                              }</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-fg-60">{
                                (() => {
                                  const planName = (req.requestedPlan?.name || "Free").toLowerCase();
                                  let badgeClass = "bg-gray-100 text-gray-600 dark:bg-gray-900/40 dark:text-gray-200";
                                  if (planName === "free") badgeClass = "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/40 dark:text-yellow-200";
                                  else if (planName === "pro") badgeClass = "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-200";
                                  else if (planName === "enterprise") badgeClass = "bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-200";
                                  return (
                                    <span className={`px-2 py-1 rounded-md text-xs font-semibold ${badgeClass}`.trim()}>
                                      {req.requestedPlan?.name || "Free"}
                                    </span>
                                  );
                                })()
                              }</td>
                              <td className="px-4 py-3 whitespace-pre-line break-words text-sm text-fg-60 max-w-xs min-w-[120px]">{req.requestMessage}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {(() => {
                                  const status = req.status;
                                  let badgeClass = "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-200";
                                  if (status === "approved") badgeClass = "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200";
                                  else if (status === "rejected") badgeClass = "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200";
                                  return (
                                    <span className={`px-2 py-1 rounded-md text-xs font-semibold ${badgeClass}`.trim()}>
                                      {status?.charAt(0).toUpperCase() + status?.slice(1)}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td className="px-4 py-3 whitespace-pre-line break-words text-sm text-fg-60 max-w-xs min-w-[120px]">{req.adminResponse}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {req.status === 'pending' ? (
                                  <div className="flex gap-2">
                                    <button
                                      className="p-2 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-200 hover:ring-2 hover:ring-green-400/40 transition-all"
                                      title="Approve"
                                      onClick={() => openModal('approve', req._id)}
                                      disabled={actionLoading === req._id}
                                    >
                                      <Check size={18} />
                                    </button>
                                    <button
                                      className="p-2 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-200 hover:ring-2 hover:ring-red-400/40 transition-all"
                                      title="Reject"
                                      onClick={() => openModal('reject', req._id)}
                                      disabled={actionLoading === req._id}
                                    >
                                      <Cross size={18} />
                                    </button>
                                  </div>
                                ) : (
                                  <span className="inline-block px-2 py-1 rounded bg-bg-40 text-fg-60 text-xs font-medium">Performed</span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )
        )}
        {error && <div className="text-red-600 mt-4">{error}</div>}
      </div>

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={createUserModalOpen}
        onClose={() => setCreateUserModalOpen(false)}
        onUserCreated={handleUserCreated}
      />

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={bulkUploadModalOpen}
        onClose={() => setBulkUploadModalOpen(false)}
        onUploadSuccess={handleBulkUploadSuccess}
      />

      {/* Admin Response Modal (unchanged) */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white dark:bg-bg-60 rounded-xl shadow-lg p-6 w-full max-w-md border border-bd-50">
            <h3 className="text-lg font-bold mb-4">{modalType === 'approve' ? 'Approve Plan Upgrade' : 'Reject Plan Upgrade'}</h3>
            <label className="block text-sm font-medium text-fg-60 mb-1">Admin Response</label>
            <textarea
              className="w-full min-h-[80px] border border-bd-50 bg-bg-50 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-ac-02 mb-2"
              value={adminResponse}
              onChange={e => setAdminResponse(e.target.value)}
              disabled={actionLoading === modalRequestId || confirming}
            />
            {!confirming ? (
              <div className="flex gap-2 justify-end">
                <button
                  className="px-4 py-2 rounded bg-bg-50 text-fg-60 font-semibold hover:bg-bg-40"
                  onClick={closeModal}
                  disabled={actionLoading === modalRequestId}
                >
                  Cancel
                </button>
                <button
                  className={`px-4 py-2 rounded ${modalType === 'approve' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-500 hover:bg-red-600 text-white'} font-semibold`}
                  onClick={handleModalSubmit}
                  disabled={actionLoading === modalRequestId}
                >
                  {modalType === 'approve' ? 'Approve' : 'Reject'}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bg-60 rounded-[22px] shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b border-bd-50">
              <h2 className="text-lg font-semibold text-fg-50">Delete User</h2>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setUserToDelete(null);
                }}
                className="p-1 hover:bg-bg-40 rounded transition-colors"
                disabled={deleteLoading}
              >
                <Cross size={20} className="text-fg-60" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-bg-50 p-4 rounded-lg">
                <p className="text-sm text-fg-60 mb-4">
                  Are you sure you want to delete this user? This action cannot be undone.
                </p>
                <div className="space-y-2">
                  <div>
                    <span className="text-xs font-medium text-fg-60">User Name:</span>
                    <p className="text-sm text-fg-50 font-medium">{userToDelete.name}</p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-fg-60">Email:</span>
                    <p className="text-sm text-fg-50">{userToDelete.email}</p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-fg-60">Account Type:</span>
                    <p className="text-sm text-fg-50 capitalize">{userToDelete.accountType || 'regular'}</p>
                  </div>
                  {userToDelete.sponsorInfo?.organization && (
                    <div>
                      <span className="text-xs font-medium text-fg-60">Organization:</span>
                      <p className="text-sm text-fg-50">{userToDelete.sponsorInfo.organization}</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-red-600 dark:text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-800 dark:text-red-500 mb-1">
                      Warning: This action cannot be undone
                    </p>
                    <p className="text-xs text-red-700 dark:text-red-400">
                      Deleting this user will permanently remove all their data, including chat history, threads, and usage records.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-bd-50">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                  }}
                  className="px-4 py-2 text-sm rounded-md bg-bg-40 text-fg-60 hover:bg-bg-30 transition-colors"
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteUser}
                  disabled={deleteLoading}
                  className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Delete User
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && selectedUsers.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bg-60 rounded-[22px] shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b border-bd-50">
              <h2 className="text-lg font-semibold text-fg-50">Delete Multiple Users</h2>
              <button
                onClick={() => {
                  setShowBulkDeleteModal(false);
                }}
                className="p-1 hover:bg-bg-40 rounded transition-colors"
                disabled={bulkDeleteLoading}
              >
                <Cross size={20} className="text-fg-60" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-bg-50 p-4 rounded-lg">
                <p className="text-sm text-fg-60 mb-4">
                  Are you sure you want to delete {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''}? This action cannot be undone.
                </p>
                <div className="space-y-2">
                  <div>
                    <span className="text-xs font-medium text-fg-60">Selected Users:</span>
                    <p className="text-sm text-fg-50 font-medium">{selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''}</p>
                  </div>
                </div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-red-600 dark:text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-800 dark:text-red-500 mb-1">
                      Warning: This action cannot be undone
                    </p>
                    <p className="text-xs text-red-700 dark:text-red-400">
                      Deleting these users will permanently remove all their data, including chat history, threads, and usage records.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-bd-50">
                <button
                  onClick={() => {
                    setShowBulkDeleteModal(false);
                  }}
                  className="px-4 py-2 text-sm rounded-md bg-bg-40 text-fg-60 hover:bg-bg-30 transition-colors"
                  disabled={bulkDeleteLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmBulkDelete}
                  disabled={bulkDeleteLoading}
                  className="px-4 py-2 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkDeleteLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Delete {selectedUsers.length} User{selectedUsers.length > 1 ? 's' : ''}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndividualsTab; 